// Sticky add-to-cart bar (snippets/product-sticky-bar.liquid).
// Shows when the main buy buttons are above the viewport, hides again near the footer, and adds the selected
// variant by clicking the main form's submit button, so Dawn's product-form handles the cart and its drawer.
if (!customElements.get('product-sticky-bar')) {
  customElements.define(
    'product-sticky-bar',
    class ProductStickyBar extends HTMLElement {
      constructor() {
        super();
        this.pastButtons = false;
        this.nearFooter = false;
        this.onClick = () => this.mainButton?.click();
      }

      connectedCallback() {
        const section = this.dataset.section;
        this.mainButton = document.getElementById(`ProductSubmitButton-${section}`);
        this.addButton = this.querySelector('[data-sticky-add]');
        this.label = this.querySelector('[data-sticky-label]');
        this.priceBox = this.querySelector('[data-sticky-price]');
        this.thumb = this.querySelector('[data-sticky-thumb]');
        if (!this.mainButton || !this.addButton) return;

        this.addButton.addEventListener('click', this.onClick);
        this.observeVisibility();
        this.observeMainForm(section);

        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId === section) this.updateThumb(data.variant);
        });
        this.syncButton();
      }

      disconnectedCallback() {
        window.cancelAnimationFrame(this.frame);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.formObserver?.disconnect();
        this.unsubscribeVariant?.();
      }

      // The bar is shown once the main submit button has scrolled above the viewport (not while it is still below
      // it) and hides while the footer is on screen. Positions are read on scroll and resize instead of with an
      // IntersectionObserver, because a fast jump from "below the viewport" to "above it" never changes the
      // intersection state and would raise no event.
      observeVisibility() {
        this.frame = 0;
        this.onScroll = () => {
          if (this.frame) return;
          this.frame = window.requestAnimationFrame(() => {
            this.frame = 0;
            this.measure();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        this.measure();
      }

      measure() {
        const footer = document.querySelector('footer');
        this.pastButtons = this.mainButton.getBoundingClientRect().bottom < 0;
        this.nearFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
        this.render();
      }

      render() {
        const visible = this.pastButtons && !this.nearFooter;
        this.classList.toggle('is-visible', visible);
        this.toggleAttribute('inert', !visible);
        this.setAttribute('aria-hidden', String(!visible));
      }

      // Keeps the button state and the price in step with the main form, whichever way Dawn updates it.
      observeMainForm(section) {
        const price = document.getElementById(`price-${section}`);
        this.formObserver = new MutationObserver(() => {
          this.syncButton();
          this.syncPrice(price);
        });
        this.formObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          characterData: true,
          subtree: true,
        });
        if (price) this.formObserver.observe(price, { childList: true, subtree: true, characterData: true });
      }

      syncButton() {
        const mainLabel = this.mainButton.querySelector('span');
        if (mainLabel && this.label) this.label.textContent = mainLabel.textContent.trim();
        this.addButton.disabled = this.mainButton.hasAttribute('disabled');
        this.addButton.classList.toggle('is-loading', this.mainButton.classList.contains('loading'));
      }

      syncPrice(price) {
        const source = price?.querySelector('.price');
        const target = this.priceBox?.querySelector('.price');
        if (source && target && target.outerHTML !== source.outerHTML) target.replaceWith(source.cloneNode(true));
      }

      updateThumb(variant) {
        const src = variant?.featured_media?.preview_image?.src;
        if (!src || !this.thumb) return;
        const image = this.thumb.querySelector('img');
        if (!image) return;
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', '120');
        image.removeAttribute('srcset');
        image.src = url.toString();
      }
    }
  );
}
